import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { endUserLabel } from "../lib/liveBreakStaff";
import { useTRPC } from "../lib/trpc";
import { ShopperPickerDialog } from "./ShopperPickerDialog";

/**
 * Puts a shopper on the Host Allow List (`liveBreaks.hosts.allow`, editors): a search of the
 * environment's end users, each with an Add button. Shoppers already on the loaded list
 * (`allowedIds`) say so instead.
 */
export function AddLiveBreakHostDialog({ allowedIds }: { allowedIds: ReadonlySet<string> }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const allow = useMutation(
    trpc.liveBreaks.hosts.allow.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
      },
    }),
  );
  return (
    <ShopperPickerDialog
      triggerLabel="Add host"
      title="Add a host"
      description="Find a shopper in this environment by username, external id or email. Once they're on the list they can start live breaks while it is enforced."
      actionLabel="Add"
      pending={allow.isPending}
      pickedLabel={(user) => (allowedIds.has(user.id) ? "On the list" : undefined)}
      onPick={(user) =>
        allow
          .mutateAsync({ endUserId: user.id })
          .then(() => toast.success(`${endUserLabel(user)} is on the Host Allow List.`))
      }
    />
  );
}

/** Puts this one end user on the Host Allow List (`liveBreaks.hosts.allow`, editors). */
export function AllowLiveBreakHostButton({
  host,
  label = "Add to the Host Allow List",
  variant = "outline",
}: {
  host: { endUserId: string; username: string | null; externalId: string };
  label?: string;
  /** Outline in a page header, ghost in a table row. */
  variant?: "outline" | "ghost";
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const allow = useMutation(
    trpc.liveBreaks.hosts.allow.mutationOptions({
      onSuccess: () => {
        toast.success(`${endUserLabel(host)} is on the Host Allow List.`);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
      },
    }),
  );
  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      disabled={allow.isPending}
      onClick={() => allow.mutate({ endUserId: host.endUserId })}
    >
      {allow.isPending ? "Adding…" : label}
    </Button>
  );
}

/**
 * Takes a host off the Host Allow List after a confirmation (`liveBreaks.hosts.disallow`,
 * editors). It stops only their next live break: one they have open keeps running, and their
 * history and commission stay theirs.
 */
export function RemoveLiveBreakHostButton({
  host,
}: {
  host: { endUserId: string; username: string | null; externalId: string };
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const name = endUserLabel(host);
  const disallow = useMutation(
    trpc.liveBreaks.hosts.disallow.mutationOptions({
      onSuccess: () => {
        toast.success(`Removed ${name} from the Host Allow List.`);
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
      },
    }),
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
        Remove
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Remove ${name} from the Host Allow List?`}</AlertDialogTitle>
          <AlertDialogDescription>
            While the list is enforced they can't start a new live break. Live breaks they already
            have open keep running, and they keep their history and the commission they have earned.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={disallow.isPending}
            onClick={() => disallow.mutate({ endUserId: host.endUserId })}
          >
            {disallow.isPending ? "Removing…" : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
